/** In-store pickup configuration and order snapshot types. */

import type { FulfilmentMethod } from "./types.js";
import { coerceStoredJsonValue } from "./coerce-stored-json.js";

export type { FulfilmentMethod };

/** JS Date#getDay(): 0 = Sunday … 6 = Saturday */
export type PickupWeekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface PickupTimeWindow {
  /** Days this window applies (0 = Sun … 6 = Sat). */
  days: PickupWeekday[];
  /** 24h time, e.g. "19:00". */
  start: string;
  /** 24h time, e.g. "22:00" — last selectable slot ends at or before this time. */
  end: string;
}

export const PICKUP_WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

export const PICKUP_SLOT_MINUTES = 30;

/** Pickup hours are interpreted in Malaysia time (UTC+8, no DST). */
export const PICKUP_TIMEZONE_OFFSET_MINUTES = 8 * 60;

export interface PickupZonedParts {
  year: number;
  month: number;
  day: number;
  weekday: PickupWeekday;
  minutes: number;
}

/** Wall-clock parts for pickup scheduling (Asia/Kuala_Lumpur). */
export function pickupUtcToParts(instant: Date): PickupZonedParts {
  const shifted = new Date(instant.getTime() + PICKUP_TIMEZONE_OFFSET_MINUTES * 60_000);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    weekday: shifted.getUTCDay() as PickupWeekday,
    minutes: shifted.getUTCHours() * 60 + shifted.getUTCMinutes(),
  };
}

export function pickupWallClockToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): Date {
  return new Date(
    Date.UTC(year, month - 1, day, hour, minute) - PICKUP_TIMEZONE_OFFSET_MINUTES * 60_000,
  );
}

export function startOfPickupCalendarDay(instant: Date): Date {
  const p = pickupUtcToParts(instant);
  return pickupWallClockToUtc(p.year, p.month, p.day, 0, 0);
}

export function pickupCalendarDayAdd(dayStartUtc: Date, days: number): Date {
  const p = pickupUtcToParts(dayStartUtc);
  const anchor = pickupWallClockToUtc(p.year, p.month, p.day, 12, 0);
  const next = pickupUtcToParts(new Date(anchor.getTime() + days * 86_400_000));
  return pickupWallClockToUtc(next.year, next.month, next.day, 0, 0);
}

export function samePickupCalendarDay(a: Date, b: Date): boolean {
  const pa = pickupUtcToParts(a);
  const pb = pickupUtcToParts(b);
  return pa.year === pb.year && pa.month === pb.month && pa.day === pb.day;
}

/** Default display slots when no per-location windows are configured. */
const DEFAULT_UNRESTRICTED_WINDOW: PickupTimeWindow = {
  days: [0, 1, 2, 3, 4, 5, 6],
  start: "08:00",
  end: "21:00",
};

export interface PickupLocation {
  id: string;
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  phone?: string;
  instructions?: string;
  /** When omitted or empty, any time within global lead/max applies. */
  availability?: PickupTimeWindow[];
  wazeUrl?: string;
  googleMapsUrl?: string;
  active: boolean;
}

/** Snapshot stored on Order.pickupDetails at checkout. */
export interface OrderPickupDetails {
  locationId: string;
  name: string;
  address1: string;
  address2?: string | null;
  city: string;
  state: string;
  postcode: string;
  country: string;
  phone?: string | null;
  instructions?: string | null;
  wazeUrl?: string | null;
  googleMapsUrl?: string | null;
  scheduledAt: string;
}

export interface PickupOptionsPublic {
  enabled: boolean;
  minLeadHours: number;
  maxDaysAhead: number;
  locations: PickupLocationPublic[];
}

export interface PickupLocationPublic {
  id: string;
  name: string;
  address1: string;
  address2?: string | null;
  city: string;
  state: string;
  postcode: string;
  country: string;
  phone?: string | null;
  instructions?: string | null;
  availability?: PickupTimeWindow[];
  wazeUrl?: string | null;
  googleMapsUrl?: string | null;
}

export function parseHm(time: string): number | null {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(time.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

export function formatHm(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function parsePickupTimeWindows(raw: unknown): PickupTimeWindow[] | undefined {
  if (raw == null) return undefined;
  if (!Array.isArray(raw)) return undefined;
  const out: PickupTimeWindow[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    if (!Array.isArray(r.days)) continue;
    const days = r.days
      .map((d) => (typeof d === "number" ? Math.trunc(d) : Number(d)))
      .filter((d): d is PickupWeekday => d >= 0 && d <= 6);
    const start = typeof r.start === "string" ? r.start.trim() : "";
    const end = typeof r.end === "string" ? r.end.trim() : "";
    if (!days.length || !start || !end) continue;
    if (parseHm(start) == null || parseHm(end) == null) continue;
    out.push({ days, start: formatHm(parseHm(start)!), end: formatHm(parseHm(end)!) });
  }
  return out.length ? out : [];
}

export function hasPickupAvailabilityRestrictions(
  windows: PickupTimeWindow[] | undefined,
): boolean {
  return Boolean(windows && windows.length > 0);
}

function effectivePickupWindows(windows: PickupTimeWindow[] | undefined): PickupTimeWindow[] {
  if (hasPickupAvailabilityRestrictions(windows)) return windows!;
  return [DEFAULT_UNRESTRICTED_WINDOW];
}

export function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function sameLocalDay(a: Date, b: Date): boolean {
  return startOfLocalDay(a).getTime() === startOfLocalDay(b).getTime();
}

/** Earliest/latest allowed pickup instants from global lead-time settings. */
export function pickupScheduleBounds(
  now: Date,
  minLeadHours: number,
  maxDaysAhead: number,
): { minAt: Date; maxAt: Date } {
  const minAt = new Date(now.getTime() + minLeadHours * 3_600_000);
  const lastDay = pickupCalendarDayAdd(startOfPickupCalendarDay(now), maxDaysAhead);
  const last = pickupUtcToParts(lastDay);
  const maxAt = new Date(pickupWallClockToUtc(last.year, last.month, last.day, 23, 59).getTime() + 59_999);
  return { minAt, maxAt };
}

function roundUpToSlot(d: Date, slotMinutes: number): Date {
  const x = new Date(d);
  const total = x.getHours() * 60 + x.getMinutes();
  const rem = total % slotMinutes;
  if (rem !== 0 || x.getSeconds() > 0 || x.getMilliseconds() > 0) {
    const next = total + (slotMinutes - rem);
    x.setHours(Math.floor(next / 60), next % 60, 0, 0);
  } else {
    x.setSeconds(0, 0);
  }
  return x;
}

export function isPickupSlotAllowed(
  scheduledAt: Date,
  windows: PickupTimeWindow[] | undefined,
): boolean {
  const { weekday, minutes } = pickupUtcToParts(scheduledAt);
  for (const w of effectivePickupWindows(windows)) {
    if (!w.days.includes(weekday)) continue;
    const start = parseHm(w.start);
    const end = parseHm(w.end);
    if (start == null || end == null || end <= start) continue;
    const lastStart = end - PICKUP_SLOT_MINUTES;
    if (minutes >= start && minutes <= lastStart) return true;
  }
  return false;
}

export function listPickupSlotsForDay(
  day: Date,
  windows: PickupTimeWindow[] | undefined,
  minAt: Date,
  maxAt: Date,
  slotMinutes = PICKUP_SLOT_MINUTES,
): Date[] {
  const dayParts = pickupUtcToParts(day);
  const slots: Date[] = [];

  for (const w of effectivePickupWindows(windows)) {
    const startMin = parseHm(w.start);
    const endMin = parseHm(w.end);
    if (startMin == null || endMin == null || endMin <= startMin) continue;
    if (!w.days.includes(dayParts.weekday)) continue;

    for (let m = startMin; m <= endMin - slotMinutes; m += slotMinutes) {
      const slot = pickupWallClockToUtc(
        dayParts.year,
        dayParts.month,
        dayParts.day,
        Math.floor(m / 60),
        m % 60,
      );
      if (slot.getTime() < minAt.getTime() || slot.getTime() > maxAt.getTime()) continue;
      slots.push(slot);
    }
  }

  slots.sort((a, b) => a.getTime() - b.getTime());
  return [...new Map(slots.map((s) => [s.getTime(), s])).values()];
}

export function listAvailablePickupDays(
  now: Date,
  minLeadHours: number,
  maxDaysAhead: number,
  windows: PickupTimeWindow[] | undefined,
): Date[] {
  const { minAt, maxAt } = pickupScheduleBounds(now, minLeadHours, maxDaysAhead);
  const days: Date[] = [];
  const base = startOfPickupCalendarDay(now);
  for (let i = 0; i <= maxDaysAhead; i++) {
    const d = pickupCalendarDayAdd(base, i);
    const slots = listPickupSlotsForDay(d, windows, minAt, maxAt);
    if (slots.length > 0) days.push(d);
  }
  return days;
}

export function formatPickupTimeWindow(w: PickupTimeWindow): string {
  const dayLabels = [...w.days]
    .sort((a, b) => a - b)
    .map((d) => PICKUP_WEEKDAY_LABELS[d])
    .join(", ");
  return `${dayLabels} ${w.start}–${w.end}`;
}

export function formatPickupAvailabilitySummary(windows: PickupTimeWindow[] | undefined): string | null {
  if (!hasPickupAvailabilityRestrictions(windows)) return null;
  return windows!.map(formatPickupTimeWindow).join(" · ");
}

export function parsePickupLocations(raw: unknown): PickupLocation[] {
  raw = coerceStoredJsonValue(raw);
  if (!Array.isArray(raw)) return [];
  const out: PickupLocation[] = [];
  for (const row of raw) {
    const parsed = parsePickupLocationRow(row, { requireComplete: true });
    if (parsed) out.push(parsed);
  }
  return out;
}

/** Editor-friendly parse — keeps in-progress rows that only have an id. */
export function parsePickupLocationsDraft(raw: unknown): PickupLocation[] {
  raw = coerceStoredJsonValue(raw);
  if (!Array.isArray(raw)) return [];
  const out: PickupLocation[] = [];
  for (const row of raw) {
    const parsed = parsePickupLocationRow(row, { requireComplete: false });
    if (parsed) out.push(parsed);
  }
  return out;
}

function parseStringField(value: unknown, trim: boolean): string {
  if (typeof value !== "string") return "";
  return trim ? value.trim() : value;
}

function parseOptionalStringField(value: unknown, trim: boolean): string | undefined {
  if (typeof value !== "string") return undefined;
  if (trim) {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  return value.length > 0 ? value : undefined;
}

function parseOptionalUrlField(value: unknown, trim: boolean): string | undefined {
  const s = parseOptionalStringField(value, trim);
  if (!s) return undefined;
  return s;
}

function parsePickupLocationRow(
  row: unknown,
  opts: { requireComplete: boolean },
): PickupLocation | null {
  if (!row || typeof row !== "object") return null;
  const r = row as Record<string, unknown>;
  const trim = opts.requireComplete;
  const id = typeof r.id === "string" ? r.id.trim() : "";
  const name = parseStringField(r.name, trim);
  const address1 = parseStringField(r.address1, trim);
  if (!id) return null;
  if (opts.requireComplete && (!name || !address1)) return null;
  const countryRaw = parseStringField(r.country, trim);
  const availability = parsePickupTimeWindows(r.availability);
  return {
    id,
    name,
    address1,
    address2: parseOptionalStringField(r.address2, trim),
    city: parseStringField(r.city, trim),
    state: parseStringField(r.state, trim),
    postcode: parseStringField(r.postcode, trim),
    country: countryRaw || "MY",
    phone: parseOptionalStringField(r.phone, trim),
    instructions: parseOptionalStringField(r.instructions, trim),
    availability,
    wazeUrl: parseOptionalUrlField(r.wazeUrl, trim),
    googleMapsUrl: parseOptionalUrlField(r.googleMapsUrl, trim),
    active: r.active !== false,
  };
}

export function activePickupLocations(raw: unknown): PickupLocation[] {
  return parsePickupLocations(raw).filter((l) => l.active);
}

export function toPickupLocationPublic(loc: PickupLocation): PickupLocationPublic {
  return {
    id: loc.id,
    name: loc.name,
    address1: loc.address1,
    address2: loc.address2 ?? null,
    city: loc.city,
    state: loc.state,
    postcode: loc.postcode,
    country: loc.country,
    phone: loc.phone ?? null,
    instructions: loc.instructions ?? null,
    availability: loc.availability,
    wazeUrl: loc.wazeUrl ?? null,
    googleMapsUrl: loc.googleMapsUrl ?? null,
  };
}

export function formatPickupAddress(details: PickupLocationPublic | OrderPickupDetails): string {
  const parts = [
    details.address1,
    details.address2,
    [details.postcode, details.city].filter(Boolean).join(" "),
    details.state,
    details.country,
  ].filter(Boolean);
  return parts.join(", ");
}

export function validatePickupScheduledAt(
  scheduledAt: Date,
  now: Date,
  minLeadHours: number,
  maxDaysAhead: number,
  windows?: PickupTimeWindow[],
): string | null {
  const { minAt, maxAt } = pickupScheduleBounds(now, minLeadHours, maxDaysAhead);
  if (scheduledAt.getTime() <= now.getTime()) return "Pickup time must be in the future";
  if (scheduledAt.getTime() < minAt.getTime()) {
    return `Pickup must be at least ${minLeadHours} hour${minLeadHours === 1 ? "" : "s"} from now`;
  }
  if (scheduledAt.getTime() > maxAt.getTime()) {
    return `Pickup must be within ${maxDaysAhead} day${maxDaysAhead === 1 ? "" : "s"}`;
  }
  if (hasPickupAvailabilityRestrictions(windows) && !isPickupSlotAllowed(scheduledAt, windows)) {
    return "Selected time is outside this location's pickup hours";
  }
  return null;
}

export function allowedFulfilmentTransitions(
  currentStatus: string,
  fulfilmentMethod: FulfilmentMethod,
): string[] {
  if (fulfilmentMethod === "PICKUP" && currentStatus === "PACKED") {
    return ["DELIVERED"];
  }
  const base: Record<string, string[]> = {
    PAID: ["PICKED"],
    PICKED: ["PACKED"],
    PACKED: ["SHIPPED"],
    SHIPPED: ["DELIVERED"],
  };
  return base[currentStatus] ?? [];
}

export function isPickupOrder(fulfilmentMethod: string | null | undefined): boolean {
  return fulfilmentMethod === "PICKUP";
}

export function parseOrderPickupDetails(raw: unknown): OrderPickupDetails | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw as Record<string, unknown>;
  const locationId = typeof d.locationId === "string" ? d.locationId.trim() : "";
  const name = typeof d.name === "string" ? d.name.trim() : "";
  const scheduledAt = typeof d.scheduledAt === "string" ? d.scheduledAt : "";
  if (!locationId || !name || !scheduledAt) return null;
  return {
    locationId,
    name,
    address1: typeof d.address1 === "string" ? d.address1 : "",
    address2: typeof d.address2 === "string" ? d.address2 : null,
    city: typeof d.city === "string" ? d.city : "",
    state: typeof d.state === "string" ? d.state : "",
    postcode: typeof d.postcode === "string" ? d.postcode : "",
    country: typeof d.country === "string" ? d.country : "MY",
    phone: typeof d.phone === "string" ? d.phone : null,
    instructions: typeof d.instructions === "string" ? d.instructions : null,
    wazeUrl: typeof d.wazeUrl === "string" ? d.wazeUrl : null,
    googleMapsUrl: typeof d.googleMapsUrl === "string" ? d.googleMapsUrl : null,
    scheduledAt,
  };
}

export function pickupGroupKey(userId: string, details: OrderPickupDetails): string {
  return `${userId}::pickup::${details.locationId}`;
}

/** Pickup slot label in Malaysia time — use for all staff/customer pickup datetime display. */
export function formatPickupScheduledAt(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const when = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(when.getTime())) return typeof iso === "string" ? iso : "—";
  return when.toLocaleString("en-MY", {
    timeZone: "Asia/Kuala_Lumpur",
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Short group label for fulfilment lists — location name only; time lives on pickupScheduledAt. */
export function formatPickupGroupSummary(details: OrderPickupDetails): string {
  return details.name;
}

export function pickupDetailsToAddressFields(details: OrderPickupDetails): {
  name?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  postcode?: string;
  state?: string;
  country?: string;
} {
  return {
    name: details.name,
    phone: details.phone ?? undefined,
    address1: details.address1,
    address2: details.address2 ?? undefined,
    city: details.city,
    postcode: details.postcode,
    state: details.state,
    country: details.country,
  };
}
